import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import type {
  EventBus,
  PDFLinkService,
  PDFViewer,
} from 'pdfjs-dist/web/pdf_viewer.mjs';

import { loadPdfJsModules, type PdfJsModule } from './loadPdfJs';
import { getPdfSourceKey, toPdfDocumentInput } from './pdfSource';
import type { PdfFitMode } from './pdfViewerScale';
import { getPdfWorkerSrc } from './pdfWorker';
import type { PdfDocumentSource } from './types';

export type PdfDocumentPreviewStatus = 'loading' | 'ready' | 'error';

export type PdfViewerRuntimeCallbacks = {
  onDocumentReset: (initialPage: number) => void;
  onFindMatches: (event: {
    matchesCount?: { current?: number; total?: number };
  }) => void;
  onPageChanging: (pageNumber: number) => void;
  onPageRendered: (pageNumber: number) => void;
  onPagesInit: () => void;
  cleanupPageLayers: () => void;
};

type UsePdfViewerRuntimeOptions = {
  source: PdfDocumentSource | null;
  initialPage: number;
  callbacksRef: RefObject<PdfViewerRuntimeCallbacks>;
  onLoad?: (pageCount: number) => void;
  onError?: (error: Error) => void;
};

export function usePdfViewerRuntime({
  source,
  initialPage,
  callbacksRef,
  onLoad,
  onError,
}: UsePdfViewerRuntimeOptions) {
  const [status, setStatus] = useState<PdfDocumentPreviewStatus>('loading');
  const [numPages, setNumPages] = useState(0);
  const [scalePct, setScalePct] = useState(100);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [viewerReady, setViewerReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerElementRef = useRef<HTMLDivElement>(null);
  const eventBusRef = useRef<EventBus | null>(null);
  const viewerRef = useRef<PDFViewer | null>(null);
  const linkServiceRef = useRef<PDFLinkService | null>(null);
  const pdfjsRef = useRef<PdfJsModule | null>(null);
  const scrollModesRef = useRef<{
    HORIZONTAL: number;
    VERTICAL: number;
  } | null>(null);
  // Mirrors `pdf` for async teardown that must not wait on a state update.
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const loadingTaskRef = useRef<PDFDocumentLoadingTask | null>(null);
  const fitModeRef = useRef<PdfFitMode>('page-width');
  const loadTokenRef = useRef(0);
  const lifecycleCallbacksRef = useRef({ onLoad, onError });
  const pdfSourceKey = source ? getPdfSourceKey(source) : null;
  lifecycleCallbacksRef.current = { onLoad, onError };

  const detachAndDestroyPdf = useCallback(
    async (viewer?: PDFViewer | null, linkService?: PDFLinkService | null) => {
      const loadingTask = loadingTaskRef.current;
      loadingTaskRef.current = null;
      pdfRef.current = null;
      setPdf(null);
      // Runtime accepts null to detach; pdf.js typings require PDFDocumentProxy.
      viewer?.setDocument(null as unknown as PDFDocumentProxy);
      linkService?.setDocument(null);
      try {
        await loadingTask?.destroy();
      } catch {
        // A superseded loading task may already have terminated.
      }
    },
    []
  );

  const toggleScrollMode = useCallback(() => {
    const viewer = viewerRef.current;
    const scrollModes = scrollModesRef.current;
    if (!viewer || !scrollModes) {
      return;
    }
    const isHorizontal = viewer.scrollMode !== scrollModes.HORIZONTAL;
    viewer.scrollMode = isHorizontal
      ? scrollModes.HORIZONTAL
      : scrollModes.VERTICAL;
    return isHorizontal;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const viewerElement = viewerElementRef.current;
    if (!container || !viewerElement) {
      return;
    }
    let disposed = false;
    let cleanupRuntime: (() => void) | undefined;

    void loadPdfJsModules()
      .then(({ pdfjs, viewer: viewerModule }) => {
        if (disposed) {
          return;
        }
        const {
          EventBus,
          PDFFindController,
          PDFLinkService,
          PDFViewer,
          ScrollMode,
        } = viewerModule;
        const eventBus = new EventBus();
        const linkService = new PDFLinkService({ eventBus });
        const findController = new PDFFindController({
          eventBus,
          linkService,
        });
        const viewer = new PDFViewer({
          container,
          viewer: viewerElement,
          eventBus,
          linkService,
          findController,
          textLayerMode: 1,
          annotationMode: pdfjs.AnnotationMode.ENABLE,
        } as ConstructorParameters<typeof PDFViewer>[0]);
        linkService.setViewer(viewer);

        const onPagesInit = () => {
          if (fitModeRef.current) {
            viewer.currentScaleValue = fitModeRef.current;
          }
          setScalePct(Math.round(viewer.currentScale * 100));
          callbacksRef.current.onPagesInit();
        };
        const onPageChanging = (event: { pageNumber: number }) => {
          callbacksRef.current.onPageChanging(event.pageNumber);
        };
        const onScaleChanging = (event: { scale?: number }) => {
          setScalePct(Math.round((event.scale ?? viewer.currentScale) * 100));
        };
        const onPageRendered = (event: { pageNumber: number }) => {
          callbacksRef.current.onPageRendered(event.pageNumber);
        };
        const onFind = (event: {
          matchesCount?: { current?: number; total?: number };
        }) => {
          callbacksRef.current.onFindMatches(event);
        };
        eventBus.on('pagesinit', onPagesInit);
        eventBus.on('pagechanging', onPageChanging);
        eventBus.on('scalechanging', onScaleChanging);
        eventBus.on('pagerendered', onPageRendered);
        eventBus.on('updatefindmatchescount', onFind);
        eventBus.on('updatefindcontrolstate', onFind);

        pdfjsRef.current = pdfjs;
        scrollModesRef.current = ScrollMode;
        eventBusRef.current = eventBus;
        viewerRef.current = viewer;
        linkServiceRef.current = linkService;
        const cleanupPageLayers = callbacksRef.current.cleanupPageLayers;
        cleanupRuntime = () => {
          cleanupPageLayers();
          eventBus.off('pagesinit', onPagesInit);
          eventBus.off('pagechanging', onPageChanging);
          eventBus.off('scalechanging', onScaleChanging);
          eventBus.off('pagerendered', onPageRendered);
          eventBus.off('updatefindmatchescount', onFind);
          eventBus.off('updatefindcontrolstate', onFind);
          pdfjsRef.current = null;
          scrollModesRef.current = null;
          eventBusRef.current = null;
          linkServiceRef.current = null;
          viewerRef.current = null;
          void detachAndDestroyPdf(viewer, linkService);
        };
        setViewerReady(true);
      })
      .catch((cause: unknown) => {
        if (disposed) {
          return;
        }
        const error =
          cause instanceof Error
            ? cause
            : new Error('Could not initialize PDF viewer');
        setStatus('error');
        lifecycleCallbacksRef.current.onError?.(error);
      });

    return () => {
      disposed = true;
      cleanupRuntime?.();
    };
  }, [callbacksRef, detachAndDestroyPdf]);

  useEffect(() => {
    const viewer = viewerRef.current;
    const linkService = linkServiceRef.current;
    const pdfjs = pdfjsRef.current;
    if (!viewerReady || !viewer || !linkService || !pdfjs) {
      return;
    }
    const token = ++loadTokenRef.current;
    const cleanupPageLayers = callbacksRef.current.cleanupPageLayers;
    callbacksRef.current.onDocumentReset(initialPage);
    cleanupPageLayers();
    fitModeRef.current = 'page-width';
    setNumPages(0);
    setPdf(null);
    setStatus('loading');

    void (async () => {
      try {
        await detachAndDestroyPdf(viewer, linkService);
        if (!source) {
          return;
        }
        const { getAutomaticPdfWorkerSrc, getAutomaticPdfWasmUrl } =
          await import('./pdfWorkerFallback');
        if (token !== loadTokenRef.current) {
          return;
        }
        pdfjs.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc(
          getAutomaticPdfWorkerSrc()
        );
        const loadingTask = pdfjs.getDocument({
          ...toPdfDocumentInput(source),
          wasmUrl: getAutomaticPdfWasmUrl(),
        });
        loadingTaskRef.current = loadingTask;
        const loadedPdf = await loadingTask.promise;
        if (token !== loadTokenRef.current) {
          if (loadingTaskRef.current === loadingTask) {
            loadingTaskRef.current = null;
          }
          try {
            await loadingTask.destroy();
          } catch {
            // A superseded loading task may already have terminated.
          }
          return;
        }
        pdfRef.current = loadedPdf;
        setPdf(loadedPdf);
        setNumPages(loadedPdf.numPages);
        viewer.setDocument(loadedPdf);
        // pdf.js typings only allow baseUrl?: null; set the URL on the service.
        linkService.setDocument(loadedPdf);
        linkService.baseUrl = source.type === 'url' ? source.url : null;
        setStatus('ready');
        lifecycleCallbacksRef.current.onLoad?.(loadedPdf.numPages);
      } catch (cause) {
        if (token !== loadTokenRef.current) {
          return;
        }
        const error =
          cause instanceof Error ? cause : new Error('Could not load PDF');
        setStatus('error');
        lifecycleCallbacksRef.current.onError?.(error);
      }
    })();

    return () => {
      loadTokenRef.current += 1;
      cleanupPageLayers();
    };
    // The identity of byte data and URL string intentionally controls reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbacksRef, detachAndDestroyPdf, pdfSourceKey, viewerReady]);

  return {
    status,
    numPages,
    scalePct,
    pdf,
    containerRef,
    viewerElementRef,
    eventBusRef,
    viewerRef,
    fitModeRef,
    toggleScrollMode,
  };
}
